import { inject, Pipe, type PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { resolveEngineText } from '@app/quote-engine';

/**
 * Translates a message produced by the quote engine (a blocker, an alert, a missing field, a prod note…).
 * The engine returns language-free message keys plus parameters; legacy plain text (frozen results written
 * before the keys existed) is displayed as stored. Multi-line blocks resolve line by line. Impure on purpose:
 * the translations may load after the first render.
 */
@Pipe({ name: 'engineMessage', standalone: true, pure: false })
export class EngineMessagePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  /**
   * Translates one engine message (or a multi-line block).
   * @param message Encoded engine message, a bare i18n key, legacy text, or several lines mixed
   * @returns The text to display
   */
  transform(message: string | null | undefined): string {
    if (!message) return '';
    return resolveEngineText(
      message,
      (key, params) => this.translate.instant(key, params),
      this.translate.getCurrentLang() ?? this.translate.getFallbackLang() ?? undefined,
    );
  }
}
